import { Controller } from "@hotwired/stimulus"
import { formatTND } from "../lib/currency"

// Lignes "produit" du formulaire de vente flash : ajout/suppression
// dynamique (nested attributes), sélecteur de produit avec recherche
// (combobox client, indispensable avec 400+ parfums en prod — un
// <select> natif avec autant d'options est inutilisable), et aperçu en
// direct du prix normal / prix après remise.
export default class extends Controller {
  static targets = [
    "items", "template", "row",
    "catalog", "picker", "dropdown", "searchInput", "results",
    "parfumIdInput", "variantSelect",
    "pricePreview", "previewNormal", "previewPromo"
  ]

  connect() {
    this.parseCatalog()
    this.rowTargets.forEach((row) => this.updatePriceInfoForRow(row))
    this.updateRemoveButtonsState()
    this.boundCloseOnOutsideClick = this.closeOnOutsideClick.bind(this)
    document.addEventListener("click", this.boundCloseOnOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this.boundCloseOnOutsideClick)
  }

  parseCatalog() {
    try {
      this.catalog = JSON.parse(this.catalogTarget.textContent || "[]")
    } catch (e) {
      this.catalog = []
    }
  }

  // ---------------------------------------------------------------- Lignes

  add(event) {
    event.preventDefault()
    const newId = Date.now()
    const html = this.templateTarget.innerHTML.replace(/NEW_RECORD/g, newId)
    const wrapper = document.createElement("div")
    wrapper.innerHTML = html.trim()
    const row = wrapper.firstElementChild
    this.itemsTarget.appendChild(row)
    this.updateRemoveButtonsState()
  }

  remove(event) {
    event.preventDefault()
    const row = event.currentTarget.closest('[data-flash-sale-items-target="row"]')
    if (!row) return

    const destroyInput = row.querySelector('input[name*="[_destroy]"]')
    if (destroyInput && destroyInput.disabled) return // sécurité : dernier produit

    if (destroyInput) {
      destroyInput.value = "1"
      row.hidden = true
    } else {
      row.remove()
    }
    this.updateRemoveButtonsState()
  }

  updateRemoveButtonsState() {
    const rows = this.rowTargets
    const activeRows = rows.filter((row) => {
      const destroyInput = row.querySelector('input[name*="[_destroy]"]')
      return !destroyInput || destroyInput.value !== "1"
    })
    const lastOneLeft = activeRows.length <= 1

    rows.forEach((row) => {
      const destroyInput = row.querySelector('input[name*="[_destroy]"]')
      const isActive = !destroyInput || destroyInput.value !== "1"
      const removeBtn = row.querySelector('[data-action*="flash-sale-items#remove"]')
      if (!removeBtn) return

      const shouldDisable = isActive && lastOneLeft
      removeBtn.disabled = shouldDisable
      removeBtn.title = shouldDisable ? "Impossible de supprimer : il faut garder au moins un produit" : ""
      if (destroyInput) destroyInput.disabled = shouldDisable
    })
  }

  // ---------------------------------------------------------- Sélecteur produit

  togglePicker(event) {
    event.preventDefault()
    const picker = event.currentTarget.closest('[data-flash-sale-items-target="picker"]')
    const dropdown = picker.querySelector('[data-flash-sale-items-target="dropdown"]')
    const alreadyOpen = !dropdown.hidden

    // Ferme tous les autres pickers ouverts avant d'ouvrir celui-ci.
    this.pickerTargets.forEach((p) => {
      p.querySelector('[data-flash-sale-items-target="dropdown"]').hidden = true
    })

    if (!alreadyOpen) {
      dropdown.hidden = false
      const input = dropdown.querySelector('[data-flash-sale-items-target="searchInput"]')
      input.value = ""
      this.renderResults(picker, this.catalog.slice(0, 40))
      input.focus()
    }
  }

  closeOnOutsideClick(event) {
    this.pickerTargets.forEach((picker) => {
      if (!picker.contains(event.target)) {
        picker.querySelector('[data-flash-sale-items-target="dropdown"]').hidden = true
      }
    })
  }

  filterCatalog(event) {
    const picker = event.currentTarget.closest('[data-flash-sale-items-target="picker"]')
    const query = event.currentTarget.value.trim().toLowerCase()

    const matches = query.length === 0
      ? this.catalog.slice(0, 40)
      : this.catalog.filter((p) =>
          p.name.toLowerCase().includes(query) || (p.brand || "").toLowerCase().includes(query)
        ).slice(0, 40)

    this.renderResults(picker, matches)
  }

  renderResults(picker, products) {
    const results = picker.querySelector('[data-flash-sale-items-target="results"]')

    if (products.length === 0) {
      results.innerHTML = `<div class="flash-product-result-empty">Aucun produit trouvé</div>`
      return
    }

    results.innerHTML = products.map((p) => `
      <button type="button" class="flash-product-result" data-product-id="${p.id}">
        ${p.image_url
          ? `<img src="${p.image_url}" class="flash-product-result-thumb" alt="">`
          : `<span class="flash-product-result-thumb flash-product-result-thumb--empty"><i class="fas fa-flask"></i></span>`}
        <span class="flash-product-result-info">
          <span class="flash-product-result-name">${this.escapeHtml(p.name)}</span>
          <span class="flash-product-result-brand">${this.escapeHtml(p.brand || "")}</span>
        </span>
      </button>
    `).join("")

    results.querySelectorAll(".flash-product-result").forEach((btn) => {
      btn.addEventListener("click", () => {
        const product = this.catalog.find((p) => String(p.id) === btn.dataset.productId)
        if (product) this.selectProduct(picker, product)
      })
    })
  }

  selectProduct(picker, product) {
    const row = picker.closest('[data-flash-sale-items-target="row"]')
    const chip = picker.querySelector(".flash-product-chip")
    const parfumIdInput = row.querySelector('[data-flash-sale-items-target="parfumIdInput"]')
    const variantSelect = row.querySelector('[data-flash-sale-items-target="variantSelect"]')

    parfumIdInput.value = product.id

    chip.classList.remove("is-empty")
    chip.innerHTML = `
      ${product.image_url
        ? `<img src="${product.image_url}" class="flash-product-chip-thumb" alt="">`
        : `<span class="flash-product-chip-thumb flash-product-chip-thumb--empty"><i class="fas fa-flask"></i></span>`}
      <span class="flash-product-chip-name">${this.escapeHtml(product.name)}</span>
      <i class="fas fa-chevron-down flash-product-chip-caret"></i>
    `

    // Reconstruit le select "Taille" avec uniquement les variantes de ce
    // produit (au lieu de pré-générer des centaines d'options pour tous
    // les produits à l'avance).
    const blankOption = `<option value="">Toutes les tailles</option>`
    const variantOptions = product.variants.map((v) =>
      `<option value="${v.id}" data-price="${v.price}">${this.escapeHtml(v.size)}</option>`
    ).join("")
    variantSelect.innerHTML = blankOption + variantOptions

    picker.querySelector('[data-flash-sale-items-target="dropdown"]').hidden = true
    this.updatePriceInfoForRow(row)
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  // ---------------------------------------------------------- Aperçu du prix

  updatePriceInfo(event) {
    const row = event.currentTarget.closest('[data-flash-sale-items-target="row"]')
    if (row) this.updatePriceInfoForRow(row)
  }

  updatePriceInfoForRow(row) {
    const variantSelect = row.querySelector('[data-flash-sale-items-target="variantSelect"]')
    const discountType = row.querySelector('select[name*="[discount_type]"]')?.value
    const discountValue = parseFloat(row.querySelector('input[name*="[discount_value]"]')?.value)
    const preview = row.querySelector('[data-flash-sale-items-target="pricePreview"]')
    const normalEl = row.querySelector('[data-flash-sale-items-target="previewNormal"]')
    const promoEl = row.querySelector('[data-flash-sale-items-target="previewPromo"]')
    if (!preview) return

    const selectedOption = variantSelect?.options[variantSelect.selectedIndex]
    let normalPrice = null
    let label = ""

    if (selectedOption && selectedOption.value && selectedOption.dataset.price) {
      normalPrice = parseFloat(selectedOption.dataset.price)
      label = this.formatCurrency(normalPrice)
    } else if (variantSelect && variantSelect.options.length > 1) {
      // "Toutes les tailles" : on affiche la fourchette de prix du produit.
      const prices = [...variantSelect.options].slice(1).map((o) => parseFloat(o.dataset.price)).filter((n) => !isNaN(n))
      if (prices.length > 0) {
        normalPrice = Math.min(...prices)
        const max = Math.max(...prices)
        label = prices.length > 1 && max !== normalPrice
          ? `${this.formatCurrency(normalPrice)} – ${this.formatCurrency(max)}`
          : this.formatCurrency(normalPrice)
      }
    }

    if (normalPrice === null) {
      preview.hidden = true
      return
    }

    preview.hidden = false
    normalEl.textContent = label

    if (!discountType || isNaN(discountValue) || discountValue <= 0) {
      promoEl.textContent = "—"
      return
    }

    const promoPrice = discountType === "percentage"
      ? normalPrice * (1 - discountValue / 100)
      : discountValue

    promoEl.textContent = this.formatCurrency(promoPrice)
    promoEl.classList.toggle("flash-price-preview-promo--invalid", promoPrice >= normalPrice)
  }

  formatCurrency(amount) {
    return formatTND(amount)
  }
}
